import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { OptionType } from '@/shared/api/BaseAPI.model'
import Alert from '@/shared/components/Alert/Alert'
import Select from '@/shared/components/Select/Select'
import Skeleton from '@/shared/components/Skeleton/Skeleton'
import { PlaceSuggestion } from '../api/GoogleLocationAPI.model'
import { MAX_QUERY_LENGTH, MIN_QUERY_LENGTH } from '../config'
import { usePlaceSearch } from '../hooks/usePlaceSearch'
import { RequestFailure, rateLimitMessage } from '../utils/requestFailure'
import styles from './GoogleLocation.module.scss'

/** The row under the suggestions that searches beyond restaurants. */
const WIDER = '__wider__'

interface PlaceSearchProps {
  /** The pick, with the token that closes its session. */
  onPick: (suggestion: PlaceSuggestion, sessionToken: string) => void
  isPicking: boolean
  /** A place is already connected, and keeps working whatever search does. */
  isConnected: boolean
}

/**
 * The venue, found by name through our backend.
 *
 * The shared Select is a WAI-ARIA combobox (arrows, Enter, Escape,
 * `aria-activedescendant`), so the "search wider" link is one of its rows
 * rather than a button under it: a row is reached with the arrows, a button
 * in a floating list is not reachable by keyboard at all.
 */
function PlaceSearch({ onPick, isPicking, isConnected }: PlaceSearchProps) {
  const { t } = useTranslation()
  const { state, search } = usePlaceSearch()
  // Escape and a click away close the list until the next keystroke.
  const [isDismissed, setIsDismissed] = useState(false)
  const [isFailureClosed, setIsFailureClosed] = useState(false)
  // When the failure on screen arrived, so a wait read out later is what is
  // left of it rather than the whole of it again.
  const failedAt = useMemo(
    () => (state.failure ? Date.now() : 0),
    [state.failure]
  )

  useEffect(() => setIsFailureClosed(false), [state.failure])

  const offerWider = state.status === 'done' && state.restrictToFood
  const options: OptionType[] = [
    ...state.suggestions.map(suggestion => ({
      value: suggestion.placeId,
      label: suggestion.mainText
    })),
    ...(offerWider
      ? [{ value: WIDER, label: t('GOOGLE_LOCATION.SEARCH.WIDER') }]
      : [])
  ]

  const isOpen =
    !isDismissed &&
    !isPicking &&
    state.text.trim().length >= MIN_QUERY_LENGTH &&
    (state.status === 'loading' || state.status === 'done')

  const renderOption = (option: OptionType) => {
    // One line whether or not anything was found: it says both at once.
    if (option.value === WIDER) {
      return (
        <span className={styles.option}>
          <span className={`body-bold-medium ${styles.wider}`}>
            {option.label}
          </span>
        </span>
      )
    }
    const suggestion = state.suggestions.find(
      candidate => candidate.placeId === option.value
    )
    return (
      <span className={styles.option}>
        <span className="body-bold-medium">{suggestion?.mainText}</span>
        {/* The list's own face, as every option in the app has it. */}
        <span className={`body-bold-small ${styles.muted}`}>
          {suggestion?.secondaryText}
        </span>
      </span>
    )
  }

  const handleChange = (option: OptionType | null) => {
    if (!option) return
    if (option.value === WIDER) {
      search.searchWider()
      return
    }
    const suggestion = state.suggestions.find(
      candidate => candidate.placeId === option.value
    )
    const sessionToken = search.endSession()
    if (suggestion && sessionToken) onPick(suggestion, sessionToken)
  }

  return (
    <div className={styles.search}>
      <Select
        label={t('GOOGLE_LOCATION.SEARCH.LABEL')}
        placeholder={t('GOOGLE_LOCATION.SEARCH.PLACEHOLDER')}
        focused
        keepCase
        hidePopupIcon
        hideClearIcon
        disabled={isPicking}
        options={options}
        value={null}
        inputValue={state.text}
        inputMaxLength={MAX_QUERY_LENGTH}
        onSearch={text => {
          setIsDismissed(false)
          // While the venue is rate limited every keystroke is dropped, so
          // the notice that says why comes back with the next one.
          if (state.failure?.kind === 'rate-limited') setIsFailureClosed(false)
          search.input(text)
        }}
        onChange={value => handleChange(Array.isArray(value) ? null : value)}
        open={isOpen}
        onOpen={() => setIsDismissed(false)}
        // Picking the wider search closes the list too, and its answer
        // belongs in it.
        onClose={(_event, reason) => {
          if (reason !== 'selectOption') setIsDismissed(true)
        }}
        loading={state.status === 'loading'}
        loadingText={
          // Shaped like the suggestions it stands in for.
          <span className={styles.loadingRows}>
            {[0, 1, 2].map(row => (
              <span key={row} className={styles.loadingRow}>
                <Skeleton variant="text" width="55%" height="1rem" />
                <Skeleton variant="text" width="35%" height="0.75rem" />
              </span>
            ))}
          </span>
        }
        noOptionsText={t('GOOGLE_LOCATION.SEARCH.NO_RESULTS')}
        renderOptionLabel={renderOption}
      />

      {state.failure && !isFailureClosed && (
        <Alert
          severity={
            state.failure.kind === 'rate-limited' ||
            state.failure.kind === 'unavailable'
              ? 'warning'
              : 'error'
          }
          message={failureMessage(state.failure, isConnected, t, failedAt)}
          onClose={() => setIsFailureClosed(true)}
        />
      )}
    </div>
  )
}

function failureMessage(
  failure: RequestFailure,
  isConnected: boolean,
  t: (key: string, options?: Record<string, unknown>) => string,
  failedAt: number
): string {
  switch (failure.kind) {
    case 'rate-limited': {
      const elapsed = Math.floor((Date.now() - failedAt) / 1000)
      const { key, values } = rateLimitMessage(
        failure.retryAfterSeconds === null
          ? null
          : failure.retryAfterSeconds - elapsed
      )
      return t(key, values)
    }
    case 'unavailable':
      return isConnected
        ? t('GOOGLE_LOCATION.SEARCH.UNAVAILABLE_CONNECTED')
        : t('GOOGLE_LOCATION.SEARCH.UNAVAILABLE')
    default:
      return t('GOOGLE_LOCATION.SEARCH.FAILED')
  }
}

export default PlaceSearch
